/**
 * QuoteMagicResume — Phase Q2 secure-token resume entry.
 *
 * URL shape:  /order-missing/quote/:public_id?t=SECURE_TOKEN
 *
 * Flow:
 *   1. Verify token via GET /api/orders/quote/resume/:public_id?t=…
 *   2. If already converted → show "already ordered" state.
 *   3. Otherwise stash the restored quote + customer identity into
 *      sessionStorage as `resumed_quote_v2` and hop to
 *      /order-missing?resume=2 which lands the customer on Step 3
 *      Review with everything hydrated.
 *   4. Invalid / expired token → friendly customer-facing message with
 *      a link back to the fresh quote flow.
 *
 * No account login required — the plaintext token IS the auth.
 */
import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuoteMagicResume() {
  const { public_id } = useParams();
  const [search] = useSearchParams();
  const token = search.get("t") || "";
  const [state, setState] = useState({ status: "loading" });
  const nav = useNavigate();

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!public_id || !token) {
        setState({ status: "invalid" });
        return;
      }
      try {
        const r = await axios.get(
          `${API}/orders/quote/resume/${encodeURIComponent(public_id)}`,
          { params: { t: token } }
        );
        if (!alive) return;
        if (r.data?.converted) {
          setState({ status: "converted",
            order_public_id: r.data.converted_order_public_id,
            converted_at:   r.data.converted_at });
          return;
        }
        // Stash the restored payload for MissingStickers to consume.
        sessionStorage.setItem("resumed_quote_v2", JSON.stringify({
          quote_public_id: r.data.public_id,
          album_slug:      r.data.album_slug,
          customer:        r.data.customer || {},
          parsed:          r.data.parsed_snapshot || {},
          quote:           r.data.quote_snapshot  || {},
          created_at:      r.data.created_at,
          // Phase Q4 — pass the plaintext token through so the
          // AbandonmentFeedback component can authorize its submissions
          // with the same magic-link token, and hydrate any prior
          // feedback so we don't re-ask the customer.
          resume_token:    token,
          feedback:        r.data.feedback || null,
          // Admin Quote Adjustments — carry the frozen admin discount /
          // custom items through the resume hop. Review renders the
          // discount line + adjusted grand total straight from the
          // server-computed `finalization.snapshot.revised_totals`
          // (falls back to `adjustments.revised_totals` when the admin
          // saved but hasn't finalized yet). Never mutates snapshot.
          admin_adjustments: r.data.finalization?.snapshot
                          || r.data.adjustments
                          || null,
          admin_finalized:   Boolean(r.data.finalization?.finalized_at),
        }));
        nav("/order-missing?resume=2", { replace: true });
      } catch (err) {
        if (!alive) return;
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail || "";
        if (status === 410 || /expired/i.test(String(detail))) {
          setState({ status: "expired" });
        } else if (status === 401) {
          setState({ status: "invalid" });
        } else if (status === 404) {
          setState({ status: "not_found" });
        } else {
          setState({ status: "error" });
        }
      }
    })();
    return () => { alive = false; };
  }, [public_id, token, nav]);

  if (state.status === "loading") {
    return (
      <main className="max-w-md mx-auto px-4 py-16 text-center" data-testid="qmr-loading">
        <Loader2 className="animate-spin inline" size={28} />
        <div className="mt-3 text-sm text-[var(--smoke)]">Opening your saved quote…</div>
      </main>
    );
  }

  if (state.status === "converted") {
    return (
      <main className="max-w-lg mx-auto px-4 py-12" data-testid="qmr-converted">
        <div className="border-2 border-emerald-800 bg-emerald-50 rounded-2xl p-6">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="text-emerald-800 shrink-0 mt-0.5" size={22} />
            <div>
              <h1 className="font-display text-2xl">This quote has already been converted into an order.</h1>
              <div className="mt-3 text-sm">
                <div>
                  <span className="font-bold">Order:</span>{" "}
                  <span className="font-mono" data-testid="qmr-order-id">
                    {state.order_public_id}
                  </span>
                </div>
                {state.converted_at && (
                  <div className="text-[var(--smoke)] mt-1">
                    Converted on {new Date(state.converted_at).toLocaleString()}
                  </div>
                )}
              </div>
              <div className="mt-5 flex gap-2 flex-wrap">
                <Link
                  to={`/orders/${state.order_public_id}`}
                  className="btn-red text-sm no-underline"
                  data-testid="qmr-view-order"
                >
                  View my order
                </Link>
                <Link to="/order-missing" className="btn-ghost text-sm no-underline">
                  Start a new quote
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  const heading =
    state.status === "expired"    ? "Your quote link has expired."
  : state.status === "invalid"    ? "That link isn't valid."
  : state.status === "not_found"  ? "We couldn't find that quote."
                                  : "We couldn't open your saved quote.";

  const testid =
    state.status === "expired"    ? "qmr-expired"
  : state.status === "invalid"    ? "qmr-invalid"
  : state.status === "not_found"  ? "qmr-not-found"
                                  : "qmr-error";

  return (
    <main className="max-w-lg mx-auto px-4 py-12" data-testid={testid}>
      <div className="border-2 border-[var(--panini-red)] bg-white rounded-2xl p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="text-[var(--panini-red)] shrink-0 mt-0.5" size={22} />
          <div>
            <h1 className="font-display text-2xl">{heading}</h1>
            <p className="mt-2 text-sm text-[var(--smoke)]">
              {state.status === "expired"
                ? "Your saved quote is no longer accessible from this link. Start a fresh quote — pricing is instant."
                : "Start a fresh quote and we'll price it in seconds."}
            </p>
            <div className="mt-5 flex gap-2 flex-wrap">
              <Link to="/order-missing" className="btn-red text-sm no-underline" data-testid="qmr-start-new-quote">
                Start a new quote
              </Link>
              <Link to="/order-missing/resume" className="btn-ghost text-sm no-underline">
                Or resume with Quote ID + email
              </Link>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
